<script>
import Layout from "@/router/layouts/main";
import { mapState } from "vuex";
import { api } from "@/state/services";
import PlantaoStepper from "@/components/plantao/PlantaoStepper";
import PlantaoPayment from "@/components/plantao/PlantaoPayment";
import PlantaoTypes from "@/components/plantao/PlantaoTypes";
import PlantaoConfirm from "@/components/plantao/PlantaoConfirm";
import PlantaoAccess from "@/components/plantao/PlantaoAccess";

export default {
  locales: {
    pt: {
    },
    es: {
    }
  },
  components: {
    Layout,
    PlantaoStepper,
    PlantaoPayment,
    PlantaoTypes,
    PlantaoConfirm,
    PlantaoAccess,
  },
  computed: {
    ...mapState({
      account: state => state.account
    }),
    // Etapa calculada sempre pelo estado da API (nunca depende de onde o cliente parou)
    currentStep() {
      if (this.state.call || this.state.creating) {
        return 4;
      }
      if (!this.selectedType) {
        return 1;
      }
      if (this.approved) {
        return 2;
      }
      if (this.state.balance >= 1) {
        return 3;
      }
      return 2;
    },
  },
  data() {
    return {
      steps: ["Atendimento", "Pagamento", "Confirmação", "Acesso"],

      loading: true,
      error: null,

      state: {
        balance: 0,
        price: 0,
        creating: false,
        call: null,
        invoice: null,
      },

      types: null,
      selectedType: "",

      generating: false,
      starting: false,
      approved: false,
      justCreated: false,
      redirectIn: 0,

      invoiceSeconds: 0,
      callSeconds: 0,

      timers: {
        poll: null,
        tick: null,
        refresh: null,
        retry: null,
        redirect: null,
      },
    };
  },
  methods: {
    // -------------------------------------------------------------------
    // Timers
    // -------------------------------------------------------------------
    clearTimer(name) {
      if (this.timers[name]) {
        clearInterval(this.timers[name]);
        clearTimeout(this.timers[name]);
        this.timers[name] = null;
      }
    },
    stopAll() {
      Object.keys(this.timers).forEach((name) => this.clearTimer(name));
    },
    retryStatus(milliseconds) {
      this.clearTimer("retry");
      this.timers.retry = setTimeout(() => this.getStatus(), milliseconds);
    },
    startTick() {
      this.clearTimer("tick");
      this.timers.tick = setInterval(() => {
        if (this.invoiceSeconds > 0) {
          this.invoiceSeconds--;
        }
        if (this.state.call && this.callSeconds > 0) {
          this.callSeconds--;
          if (this.callSeconds === 0) {
            this.getStatus();
          }
        }
      }, 1000);
    },

    // -------------------------------------------------------------------
    // Estado da página
    // -------------------------------------------------------------------
    getStatus() {
      api
        .get("telemedicina/plantao/status")
        .then((response) => {
          if (response.data.status != "success") {
            // Ex.: telemedicina inativa (validação do construtor da API)
            this.error = response.data.message || "Não foi possível carregar o plantão.";
            this.stopAll();
            return;
          }

          this.error = null;
          this.setState(response.data.plantao);
        })
        .catch(() => {
          this.$toast.error("Não foi possível carregar, tente novamente.");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setState(plantao) {
      this.state.balance = plantao.balance;
      this.state.price = Number(plantao.price || 0);
      this.state.creating = plantao.creating;
      this.state.call = plantao.call;
      this.state.invoice = plantao.invoice;

      this.invoiceSeconds = plantao.invoice ? plantao.invoice.seconds_remaining : 0;
      this.callSeconds = plantao.call ? plantao.call.seconds_remaining : 0;

      if (!plantao.call) {
        this.justCreated = false;
        this.clearTimer("redirect");
      }

      this.startTick();

      // Polling da fatura (somente leitura) enquanto aguarda pagamento
      if (plantao.invoice && !plantao.invoice.processing && plantao.balance < 1) {
        this.startPolling();
      } else {
        this.clearTimer("poll");
      }

      // PIX ou sala em geração: recarrega em 3s
      if ((plantao.invoice && plantao.invoice.processing) || (plantao.creating && !plantao.call)) {
        this.retryStatus(3000);
      }

      // Plantão ativo: recarrega a cada 60s (encerramento/prorrogação feitos pelo admin)
      this.clearTimer("refresh");
      if (plantao.call) {
        this.timers.refresh = setInterval(() => this.getStatus(), 60000);
      }

      if (!this.types) {
        this.getTypes();
      }
    },
    getTypes() {
      api
        .get("telemedicina/plantao/tipos")
        .then((response) => {
          if (response.data.status == "success") {
            this.types = response.data.list;
          }
        })
        .catch(() => {
          this.$toast.error("Não foi possível carregar os tipos de atendimento.");
        });
    },

    // -------------------------------------------------------------------
    // Etapa 2 · Pagamento
    // -------------------------------------------------------------------
    generatePix() {
      if (this.generating) {
        return;
      }
      this.generating = true;

      api
        .post("telemedicina/plantao/fatura", {
          confirm: 1,
        })
        .then((response) => {
          if (response.data.status == "success") {
            this.state.invoice = response.data.invoice;
            this.invoiceSeconds = response.data.invoice.seconds_remaining;
            this.startTick();
            this.startPolling();
          } else if (response.data.status == "processing") {
            this.state.invoice = { processing: true };
            this.retryStatus(3000);
          } else {
            this.$toast.error(response.data.message || "Não foi possível gerar o pagamento, tente novamente.");
            this.getStatus();
          }
        })
        .catch(() => {
          this.$toast.error("Não foi possível gerar o pagamento, tente novamente.");
        })
        .finally(() => {
          this.generating = false;
        });
    },
    startPolling() {
      this.clearTimer("poll");
      this.timers.poll = setInterval(() => this.pollInvoice(), 5000);
    },
    pollInvoice() {
      if (!this.state.invoice || !this.state.invoice.id) {
        return;
      }

      api
        .get("telemedicina/plantao/fatura/" + this.state.invoice.id)
        .then((response) => {
          if (response.data.status != "success") {
            return;
          }
          const status = response.data.invoice.status;
          if (status == "approved") {
            this.clearTimer("poll");
            this.approved = true;
            this.$toast.success("Pagamento confirmado! 1 crédito liberado.");
            setTimeout(() => {
              this.approved = false;
              this.getStatus();
            }, 1500);
          } else if (status == "expired" || status == "canceled") {
            this.clearTimer("poll");
            this.state.invoice = Object.assign({}, this.state.invoice, { status: "expired" });
          }
        });
    },

    // -------------------------------------------------------------------
    // Etapas 1 e 3 · Atendimento e confirmação
    // -------------------------------------------------------------------
    selectType(type) {
      this.selectedType = type;
    },
    startCall() {
      if (this.starting || !this.selectedType) {
        return;
      }
      this.starting = true;

      api
        .post("telemedicina/plantao", {
          type: this.selectedType,
        })
        .then((response) => {
          if (response.data.status == "success") {
            this.selectedType = "";
            this.state.call = {
              id: response.data.id,
              url: response.data.url,
              expiration: response.data.expiration,
              seconds_remaining: response.data.seconds_remaining,
            };
            this.callSeconds = response.data.seconds_remaining;
            this.justCreated = true;
            this.startRedirect();
            this.getStatus();
          } else if (response.data.status == "processing") {
            this.$toast.info(response.data.message);
            this.retryStatus(3000);
          } else {
            this.$toast.error(response.data.message || "Não foi possível acessar o plantão médico, tente novamente.");
            this.getStatus();
          }
        })
        .catch(() => {
          this.$toast.error("Não foi possível acessar o plantão médico, tente novamente.");
          this.getStatus();
        })
        .finally(() => {
          this.starting = false;
        });
    },

    // -------------------------------------------------------------------
    // Etapa 4 · Acesso
    // -------------------------------------------------------------------
    startRedirect() {
      this.clearTimer("redirect");
      this.redirectIn = 5;
      this.timers.redirect = setInterval(() => {
        this.redirectIn--;
        if (this.redirectIn <= 0) {
          this.clearTimer("redirect");
          this.enter();
        }
      }, 1000);
    },
    enter() {
      if (this.state.call && this.state.call.url) {
        this.clearTimer("redirect");
        window.location.href = this.state.call.url;
      }
    },
  },
  mounted() {
    // Link antigo da aba de histórico
    if (this.$route.query.tab === "historico") {
      this.$router.replace("/telemedicina/historico");
      return;
    }
    this.getStatus();
  },
  beforeDestroy() {
    this.stopAll();
  },
};
</script>

<template>
  <Layout>
    <ol class="breadcrumb">
      <li class="breadcrumb-item">
        <router-link tag="a" to="/">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" style="fill: rgba(0, 0, 0, 1);">
            <path d="M5 22h14a2 2 0 0 0 2-2v-9a1 1 0 0 0-.29-.71l-8-8a1 1 0 0 0-1.41 0l-8 8A1 1 0 0 0 3 11v9a2 2 0 0 0 2 2zm5-2v-5h4v5zm-5-8.59 7-7 7 7V20h-3v-5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v5H5z">
            </path>
          </svg>
        </router-link>
      </li>
      <li class="breadcrumb-item"><router-link tag="a" to="/telemedicina">Telemedicina</router-link></li>
    </ol>

    <div class="page-title-box d-md-flex justify-content-between mb-3">
      <div>
        <h3>Consultas Online</h3>
        <p>Fale com nossa equipe de enfermagem, disponível 24 horas por dia, e receba ajuda para agendar sua consulta com o especialista desejado.</p>
      </div>
    </div>

    <div class="row justify-content-center">
      <div class="col-lg-7">
        <div v-if="loading" class="text-center py-5">
          <b-spinner variant="default" role="status"></b-spinner>
        </div>

        <div v-else-if="error" class="card">
          <div class="card-body text-center py-5">
            <h5 class="mb-3">{{ error }}</h5>
            <router-link tag="a" to="/planos" class="btn btn-default btn-lg">Ver planos</router-link>
          </div>
        </div>

        <template v-else>
          <PlantaoStepper :step="currentStep" :steps="steps" />

          <div class="card">
            <div class="card-body">
              <div class="text-center mb-3">
                <span class="badge badge-soft-success font-size-13 p-2 px-3 text-uppercase">{{ state.balance }} crédito(s) disponível(is)</span>
              </div>

              <PlantaoTypes
                v-if="currentStep == 1"
                :types="types"
                @select-type="selectType"
              />

              <PlantaoPayment
                v-else-if="currentStep == 2"
                :price="state.price"
                :invoice="state.invoice"
                :seconds-remaining="invoiceSeconds"
                :loading="generating"
                :approved="approved"
                @generate-pix="generatePix"
              />

              <PlantaoConfirm
                v-else-if="currentStep == 3"
                :loading="starting"
                @confirm="startCall"
                @back="selectedType = ''"
              />

              <PlantaoAccess
                v-else
                :call="state.call"
                :creating="state.creating"
                :seconds-remaining="callSeconds"
                :balance="state.balance"
                :just-created="justCreated"
                :redirect-in="redirectIn"
                @enter="enter"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </Layout>
</template>
